import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SectionHeading } from '../components/shared/SectionHeading';
import { companyInfo } from '../data/mock';
import { ArrowRight, Target, Lightbulb, Shield, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function About() {
  const { t } = useLanguage();
  
  const values = [
    {
      icon: Target,
      title: t.aboutPage.values.items[0].title,
      desc: t.aboutPage.values.items[0].desc
    },
    {
      icon: Shield,
      title: t.aboutPage.values.items[1].title,
      desc: t.aboutPage.values.items[1].desc
    },
    {
      icon: Lightbulb,
      title: t.aboutPage.values.items[2].title,
      desc: t.aboutPage.values.items[2].desc
    },
    {
      icon: Users,
      title: t.aboutPage.values.items[3].title,
      desc: t.aboutPage.values.items[3].desc
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/20" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.1),transparent_70%)]" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm font-medium mb-8">
              {t.aboutPage.badge}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-6">
              {t.aboutPage.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                {t.aboutPage.titleHighlight}
              </span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
              {t.aboutPage.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.aboutPage.story.badge}
            title={t.aboutPage.story.title}
            centered
          />
          <div className="mt-8 space-y-6 text-slate-600 dark:text-slate-400 leading-relaxed text-center">
            {t.aboutPage.story.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.aboutPage.values.badge}
            title={t.aboutPage.values.title}
            subtitle={t.aboutPage.values.subtitle}
            centered
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            {values.map((value, index) => (
              <Card key={index} className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/50 text-center">
                <CardContent className="p-8">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{value.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionHeading
                badge={t.aboutPage.location.badge}
                title={t.aboutPage.location.title}
                subtitle={t.aboutPage.location.subtitle}
              />
              <div className="mt-8 space-y-4 text-slate-600 dark:text-slate-400">
                <p className="flex items-start gap-3">
                  <span className="font-semibold text-slate-900 dark:text-white min-w-[80px]">{t.contactPage.office}:</span>
                  {companyInfo.address}
                </p>
                <p className="flex items-start gap-3">
                  <span className="font-semibold text-slate-900 dark:text-white min-w-[80px]">{t.contactPage.email}:</span>
                  <a href={`mailto:${companyInfo.email}`} className="text-emerald-600 dark:text-emerald-400 hover:underline">
                    {companyInfo.email}
                  </a>
                </p>
                <p className="flex items-start gap-3">
                  <span className="font-semibold text-slate-900 dark:text-white min-w-[80px]">{t.contactPage.phone}:</span>
                  <a href={`tel:${companyInfo.phone}`} className="text-emerald-600 dark:text-emerald-400 hover:underline">
                    {companyInfo.phone}
                  </a>
                </p>
              </div>
            </div>
            
            <div className="bg-slate-200 dark:bg-slate-800 rounded-2xl aspect-video flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <Target className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-slate-500 dark:text-slate-400">Stuttgart, Germany</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.aboutPage.ctaTitle}
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            {t.aboutPage.ctaSubtitle}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 px-8 text-base">
              {t.aboutPage.getInTouch}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
